import { useEffect, useState, type FC } from 'react';
import { useCustomMutation, useInvalidate } from '@refinedev/core';
import { Modal, Tabs, Form, Button, Popconfirm, message } from 'antd';
import { Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import type { Campaign } from './CampaignTable';
import CampaignInfoTab, { type InfoFormValues } from './CampaignInfoTab';
import CampaignKpisTab, { type KpisFormValues } from './CampaignKpisTab';
import CampaignContentsTab from './CampaignContentsTab';

interface CampaignDetailModalProps {
  open: boolean;
  campaign: Campaign | null;
  onClose: () => void;
}

const CampaignDetailModal: FC<CampaignDetailModalProps> = ({ open, campaign, onClose }) => {
  const isEdit = !!campaign;
  const [infoForm] = Form.useForm<InfoFormValues>();
  const [kpisForm] = Form.useForm<KpisFormValues>();
  const [activeTab, setActiveTab] = useState('info');
  const invalidate = useInvalidate();
  const { mutate: saveCampaign } = useCustomMutation();
  const { mutate: updateCampaign } = useCustomMutation();

  useEffect(() => {
    if (!open) return;
    setActiveTab('info');
    if (campaign) {
      infoForm.setFieldsValue({
        name: campaign.name,
        channel: campaign.channel,
        startDate: campaign.startDate ? dayjs(campaign.startDate) : undefined,
        endDate: campaign.endDate ? dayjs(campaign.endDate) : undefined,
        targetPersona: campaign.targetPersona ?? undefined,
        status: campaign.status,
        description: campaign.description ?? '',
      });
      const kpis = campaign.kpis ?? {};
      kpisForm.setFieldsValue({
        impressions: (kpis.impressions as number) ?? undefined,
        clicks: (kpis.clicks as number) ?? undefined,
        engagementRate: (kpis.engagementRate as number) ?? undefined,
        leads: (kpis.leads as number) ?? undefined,
        conversions: (kpis.conversions as number) ?? undefined,
      });
    } else {
      infoForm.resetFields();
      kpisForm.resetFields();
    }
  }, [open, campaign, infoForm, kpisForm]);

  const refreshData = () => invalidate({ invalidates: ['all'] });

  const handleSave = () => {
    infoForm.validateFields().then((values) => {
      const kpisValues = kpisForm.getFieldsValue();
      const kpis: Record<string, unknown> = {};
      if (kpisValues.impressions != null) kpis.impressions = kpisValues.impressions;
      if (kpisValues.clicks != null) kpis.clicks = kpisValues.clicks;
      if (kpisValues.engagementRate != null) kpis.engagementRate = kpisValues.engagementRate;
      if (kpisValues.leads != null) kpis.leads = kpisValues.leads;
      if (kpisValues.conversions != null) kpis.conversions = kpisValues.conversions;

      const payload = {
        name: values.name, channel: values.channel,
        targetPersona: values.targetPersona ?? null,
        description: values.description ?? null,
        startDate: values.startDate?.format('YYYY-MM-DD') ?? null,
        endDate: values.endDate?.format('YYYY-MM-DD') ?? null,
        kpis: Object.keys(kpis).length > 0 ? kpis : null,
        ...(isEdit && values.status !== campaign!.status ? { status: values.status } : {}),
      };

      if (isEdit) {
        updateCampaign(
          { url: `/api/v1/admin/marketing/campaigns/${campaign!.id}`, method: 'patch', values: payload },
          {
            onSuccess: () => { message.success('Campagne modifiée'); refreshData(); onClose(); },
            onError: (err) => message.error(err?.message || 'Erreur lors de la modification'),
          },
        );
      } else {
        saveCampaign(
          { url: '/api/v1/admin/marketing/campaigns', method: 'post', values: payload },
          {
            onSuccess: () => { message.success('Campagne créée'); refreshData(); onClose(); },
            onError: () => message.error('Erreur lors de la création'),
          },
        );
      }
    });
  };

  const handleDelete = () => {
    if (!campaign) return;
    updateCampaign(
      { url: `/api/v1/admin/marketing/campaigns/${campaign.id}`, method: 'patch', values: { status: 'completed' } },
      { onSuccess: () => { message.success('Campagne terminée'); refreshData(); onClose(); } },
    );
  };

  const tabItems = [
    { key: 'info', label: 'Informations', children: <CampaignInfoTab form={infoForm} /> },
    { key: 'contents', label: 'Contenus liés', children: <CampaignContentsTab isEdit={isEdit} campaign={campaign} /> },
    { key: 'kpis', label: 'KPIs', children: <CampaignKpisTab form={kpisForm} /> },
  ];

  return (
    <Modal
      title={isEdit ? 'Modifier la campagne' : 'Nouvelle campagne'}
      open={open} onCancel={onClose} width={700} destroyOnClose
      footer={
        <div className="flex items-center justify-between">
          <div>
            {isEdit && campaign!.status !== 'completed' && (
              <Popconfirm title="Marquer comme terminée ?" onConfirm={handleDelete} okText="Oui" cancelText="Non">
                <Button danger icon={<Trash2 className="h-3.5 w-3.5" />}>Terminer</Button>
              </Popconfirm>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose}>Annuler</Button>
            <Button type="primary" onClick={handleSave}>{isEdit ? 'Modifier' : 'Créer'}</Button>
          </div>
        </div>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Modal>
  );
};

export default CampaignDetailModal;
